import React, { FunctionComponent } from 'react';
import { Chip, Tooltip } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import type { SuppressKeyboardEventParams } from 'ag-grid-community';

import {
  RUN_STATUS_DISPLAY,
  toReasonCode,
  toRunStatus,
} from './runHistoryStatus';
import type { IRunHistoryRow } from './runHistoryApi';

interface IRunStatusBadgeProps {
  status: unknown;
  reasonCode?: unknown;
}

// A reason chip is a tab stop of its own inside the cell. Let Tab move from the
// cell itself into the chip (so its focus tooltip opens) instead of skipping to
// the next cell; Tab from the chip is not suppressed and leaves normally.
export const suppressTabToReasonChip = (
  params: SuppressKeyboardEventParams<IRunHistoryRow>
): boolean => {
  const { event, data } = params;
  const target = event.target as Element | null;
  return (
    event.key === 'Tab' &&
    !event.shiftKey &&
    toReasonCode(data?.reasonCode) !== null &&
    !!target?.classList.contains('ag-cell')
  );
};

const RunStatusBadge: FunctionComponent<IRunStatusBadgeProps> = ({
  status,
  reasonCode,
}) => {
  const display = RUN_STATUS_DISPLAY[toRunStatus(status)];
  const Icon = display.icon;
  const code = toReasonCode(reasonCode);

  const chip = (
    <Chip
      size="small"
      color={display.color}
      // The tooltip replaces the native title when there is a reason code.
      title={code ? undefined : display.description}
      tabIndex={code ? 0 : undefined}
      label={display.label}
      icon={<Icon data-testid="run-status-icon" />}
      sx={
        display.background
          ? {
              bgcolor: display.background,
              color: 'common.black',
              '& .MuiChip-icon': { color: 'common.black' },
            }
          : undefined
      }
    />
  );

  if (!code) return chip;

  return (
    <>
      {/* describeChild keeps the status word as the chip name and exposes the tooltip as its description. */}
      <Tooltip
        describeChild
        title={`${display.description}. Reason code: ${code}`}
      >
        {chip}
      </Tooltip>
      <span style={visuallyHidden}>Reason code: {code}</span>
    </>
  );
};

export default RunStatusBadge;
